import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { logError, logInfo } from '../config_and_helpers/logger';
import { uploadImagesToS3 } from './product_client';
import { showToast } from '../config_and_helpers/helpers';
import OrderCache from '../config_and_helpers/order_cache';
import {
  formatUploadFinalizeErrors,
  notifyUploadFinalizeFailure,
} from '../utils/uploadFinalizeErrors';
import { isPresignedUrlsDict } from '../utils/orderFiles';
import { appendFieldsToFormData } from '../utils/multipartFormData';
import { isCanceledRequest } from '../utils/listSearchRequest';

const LOG_CONTEXT_ORDER = 'order_client';

/**
 * S3 upload + finalize for order create/update presigned_urls dict.
 *
 * @param {string} token
 * @param {Object} presignedDict
 * @param {{ poPending: Array, receiptPending: Array }} pending
 * @returns {Promise<{ success: boolean }>}
 */
async function processOrderPresignedUploads(token, presignedDict, pending) {
  if (!isPresignedUrlsDict(presignedDict)) {
    logError(LOG_CONTEXT_ORDER, 'processOrderPresignedUploads: invalid presigned_urls', {});
    return { success: false };
  }

  const poUrls = presignedDict.po_files || [];
  const receiptUrls = presignedDict.receipt_files || [];
  const poPending = pending.poPending || [];
  const receiptPending = pending.receiptPending || [];

  logInfo(LOG_CONTEXT_ORDER, 'processOrderPresignedUploads start', {
    poUrlCount: poUrls.length,
    receiptUrlCount: receiptUrls.length,
  });

  let success = true;

  if (poUrls.length > 0 && poPending.length > 0) {
    const uploadResult = await uploadPdfsToS3(poUrls, poPending);
    if (uploadResult?.uploadStatuses) {
      const finalized = await finalizePdfUploadStatus(
        token,
        uploadResult.uploadStatuses
      );
      if (!finalized?.success) success = false;
    } else {
      success = false;
    }
  }

  if (receiptUrls.length > 0 && receiptPending.length > 0) {
    const uploadResult = await uploadImagesToS3(receiptUrls, receiptPending);
    if (uploadResult?.uploadStatuses) {
      const finalized = await finalizeOrderImageUploadStatus(
        token,
        uploadResult.uploadStatuses
      );
      if (!finalized?.success) success = false;
    } else {
      success = false;
    }
  }

  return { success };
}

/**
 * Finalize the upload status of an order image.
 *
 * Sends a POST request to the backend API to update the upload status of an order image. Assuming a map structure where the keys are order image IDs and the values are the upload status, and if successful, returns a success status.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Object} uploadStatuses - An object mapping order image IDs to their upload status.
 * @returns {Promise<Object>} An object with a key "success" and a boolean value indicating whether the API call was successful. If it fails, returns an array containing error messages.
 * @throws {Error} Will throw an error if the request fails.
 */
export const finalizeOrderImageUploadStatus = async (token, uploadStatuses) => {
  try {
    // Convert the uploadStatuses object to a FormData instance
    let formData = new FormData();
    for (let key in uploadStatuses) {
      formData.append(key, uploadStatuses[key]);
    }

    // Send a POST request to the backend API to update the images' upload status
    await axios.post(
      `${BACKEND_URL}orders/update_image_upload_status/`,
      formData,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'multipart/form-data', // Since we're uploading files, we need to set the content type to 'multipart/form-data'
        },
      }
    );

    // On success, return an object indicating the success of the operation
    return { success: true };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT_ORDER, 'finalizeOrderImageUploadStatus failed', {
      status: error.response?.status,
    });
    const messages = formatUploadFinalizeErrors(errPayload);
    notifyUploadFinalizeFailure(messages);
    return { success: false, error: errPayload, messages };
  }
};

/**
 * Create a new order.
 *
 * Sends a POST request to the backend API to create a new order. If the request is successful and there are images associated with the order, each image is uploaded to the S3 bucket. Lastly, uploads the status of each image.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Object} orderData - The data for the new order.
 * @param {Array} images - An array of image objects to be associated with the order.
 * @returns {Promise<Object|Array>} An object with a key "success" indicating whether order and image uploads were successful and "toast" with a function to display a toast message. If the request fails, returns an array with error messages.
 * @throws Will throw an error if the request fails.
 */
/**
 * @param {string} token
 * @param {Object} orderData
 * @param {{ poPending?: Array, receiptPending?: Array }} [pendingFiles]
 */
export const createOrder = async (token, orderData, pendingFiles = {}) => {
  try {
    const formData = new FormData();
    appendFieldsToFormData(formData, orderData);

    const response = await axios.post(`${BACKEND_URL}orders/`, formData, {
      headers: {
        Authorization: `Token ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    let result = { success: true };
    const presignedUrls = response.data?.presigned_urls;
    const poPending = pendingFiles.poPending || [];
    const receiptPending = pendingFiles.receiptPending || [];

    if (
      (poPending.length > 0 || receiptPending.length > 0) &&
      isPresignedUrlsDict(presignedUrls)
    ) {
      const uploadOk = await processOrderPresignedUploads(token, presignedUrls, {
        poPending,
        receiptPending,
      });
      if (!uploadOk.success) result.success = false;
    } else if (poPending.length > 0 || receiptPending.length > 0) {
      result.success = false;
      logError(LOG_CONTEXT_ORDER, 'createOrder: missing presigned_urls dict', {});
    }

    const wantsSupplierNotify =
      poPending.length > 0 &&
      (orderData.notify_supplier === true ||
        orderData.notify_supplier === 'true' ||
        orderData.notify_supplier === 1 ||
        orderData.notify_supplier === '1' ||
        orderData.notify_supplier === 'yes');

    result.toast = () => {
      showToast('Order created successfully!', 'success', 'top-right', 3000);
      if (wantsSupplierNotify && result.success) {
        showToast(
          'Supplier reminder email queued.',
          'info',
          'top-right',
          4000
        );
      }
    };

    return result;
  } catch (error) {
    logError(LOG_CONTEXT_ORDER, 'createOrder failed', error.response?.data ?? error);
    return Object.values(error.response?.data || { error: error.message });
  }
};

/**
 * Update an existing order.
 *
 * Sends a PATCH request to the backend API to update a particular order denoted by orderId with the provided updatedOrderData. If the request is successful and there are images associated with the update, each image is uploaded to the S3 bucket. Lastly, uploads the status for each image.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} orderId - The ID of the order to be updated.
 * @param {Object} updatedOrderData - Order fields. Optional quote funding: `budget` OR `funding_credit_id` + `funding_amount`.
 * @param {{ poPending?: Array, receiptPending?: Array }} [pendingFiles]
 * @returns {Promise<Object|Array>} An object with a key "success" indicating whether order and image uploads were successful and "toast" with a function to display a toast message. If the request fails, returns an array with error messages.
 * @throws Will throw an error if the request fails.
 */
export const updateOrder = async (
  token,
  orderId,
  updatedOrderData,
  pendingFiles = {}
) => {
  try {
    const hasFundingPatch =
      updatedOrderData?.budget != null ||
      updatedOrderData?.funding_credit_id != null ||
      updatedOrderData?.funding_amount != null;
    if (hasFundingPatch) {
      logInfo(LOG_CONTEXT_ORDER, 'updateOrder: quote funding patch', {
        orderId,
        budget: updatedOrderData.budget ?? null,
        fundingCreditId: updatedOrderData.funding_credit_id ?? null,
        fundingAmount: updatedOrderData.funding_amount ?? null,
      });
    }

    const formData = new FormData();
    appendFieldsToFormData(formData, updatedOrderData);

    const response = await axios.patch(
      `${BACKEND_URL}orders/${orderId}/`,
      formData,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    let result = { success: true, updatedOrder: response.data };
    const presignedUrls = response.data?.presigned_urls;
    const poPending = pendingFiles.poPending || [];
    const receiptPending = pendingFiles.receiptPending || [];

    if (
      (poPending.length > 0 || receiptPending.length > 0) &&
      isPresignedUrlsDict(presignedUrls)
    ) {
      const uploadOk = await processOrderPresignedUploads(token, presignedUrls, {
        poPending,
        receiptPending,
      });
      if (!uploadOk.success) result.success = false;
    } else if (poPending.length > 0 || receiptPending.length > 0) {
      result.success = false;
      logError(LOG_CONTEXT_ORDER, 'updateOrder: missing presigned_urls dict', {
        orderId,
      });
    }

    result.toast = () =>
      showToast('Order updated successfully!', 'success', 'top-right', 3000);

    return result;
  } catch (error) {
    logError(LOG_CONTEXT_ORDER, 'updateOrder failed', error.response?.data ?? error);
    return Object.values(error.response?.data || { error: error.message });
  }
};

/**
 * Delete an existing order.
 *
 * Normal delete adjusts `product.stock`. Pass `hardDelete: true` to skip inventory
 * adjustment (`DELETE ...?hard_delete=true`) when stock would go negative.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string|number} orderId - The ID of the order to be deleted.
 * @param {{ hardDelete?: boolean }} [options]
 * @returns {Promise<
 *   | { success: true, toast: function }
 *   | { success: false, status?: number, detail?: string, error?: string }
 * >}
 */
export const deleteOrder = async (token, orderId, { hardDelete = false } = {}) => {
  const params = hardDelete ? { hard_delete: 'true' } : {};
  logInfo(LOG_CONTEXT_ORDER, 'deleteOrder start', { orderId, hardDelete });
  try {
    await axios.delete(`${BACKEND_URL}orders/${orderId}/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
      params,
    });

    logInfo(LOG_CONTEXT_ORDER, 'deleteOrder success', { orderId, hardDelete });

    const successMessage = hardDelete
      ? 'Order deleted (inventory not updated).'
      : 'Order deleted successfully!';

    return {
      success: true,
      toast: () =>
        showToast(successMessage, 'success', 'top-right', 3000),
    };
  } catch (error) {
    const status = error.response?.status;
    const data = error.response?.data || {};
    const detail =
      data.error ||
      data.detail ||
      (typeof data === 'string' ? data : error.message);
    logError(LOG_CONTEXT_ORDER, 'deleteOrder failed', {
      orderId,
      hardDelete,
      status,
      detail,
    });
    return { success: false, status, detail, error: detail };
  }
};

/**
 * Retrieve orders.
 *
 * Sends a GET request to the backend API to retrieve a list of all orders. Supports optional pagination and search parameters. If the request is successful, the orders are set in the state.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Function} setOrders - React state's setter function being used to update the Orders state in the component.
 * @param {Object} options - An optional object that can contain nextPage and/or searchInput properties.
 * @returns {Promise<Object|Array>} - An object with a boolean 'success', if the orders are successfully retrieved with either a 'reachedEnd' boolean or 'nextPage' string for next set of results; if not, it returns an array of error messages.
 * @throws Will throw an error if the request fails.
 */
export const getOrders = async (token, setOrders, options = {}) => {
  // Defaults
  const {
    searchInput = '',
    nextPage = null,
    supplierFilter = '',
    userFilter = '',
    statusFilter = '',
    sortBy = '',
    sortDirection = '',
    signal = undefined,
  } = options;

  // Construct the request URL (check if next page URL exists, otherwise request the first page)
  let url = nextPage ? nextPage : `${BACKEND_URL}orders/?page_num=1`;
  const requestStartedAt = Date.now();

  // Apply filters/sort only on first-page fetches.
  if (!nextPage) {
    // If a search input is provided add it to request URL as a query parameter
    if (searchInput) {
      url += `&search=${searchInput}`;
    }

    // If a supplier id value is provided
    if (supplierFilter) {
      url += `&supplier_filter=${supplierFilter}`;
    }
    if (userFilter) {
      url += `&user_id=${userFilter}`;
    }

    // If a status filter is provided
    if (statusFilter) {
      url += `&status_filter=${statusFilter}`;
    }
    if (sortBy) {
      url += `&sort_by=${sortBy}`;
    }
    if (sortDirection) {
      url += `&sort_direction=${sortDirection}`;
    }

    // Force a fresh first-page read after mutations (create/delete/filter changes).
    // Some environments cache identical GET URLs for a short TTL.
    url += `&_ts=${requestStartedAt}`;
    logInfo(LOG_CONTEXT_ORDER, 'getOrders first page request', {
      hasSearch: Boolean(searchInput),
      hasSupplierFilter: Boolean(supplierFilter),
      userFilter: userFilter || null,
      statusFilter: statusFilter || null,
      sortBy: sortBy || null,
      sortDirection: sortDirection || null,
      cacheBustTs: requestStartedAt,
    });
  }

  try {
    // Get the orders from the server
    // Avoid extra request headers beyond Authorization: some stacks/proxies mishandle
    // Cache-Control/Pragma on GET and surface net::ERR_FAILED in Chrome. Query `_ts`
    // above is enough to bust URL-keyed HTTP caches.
    const response = await axios.get(url, {
      headers: {
        Authorization: `Token ${token}`,
      },
      signal,
    });

    if (process.env.NODE_ENV === 'development') {
      logInfo('order_client', 'getOrders response.data', {
        requestUrl: url,
        requestStartedAt,
        nextPageRequested: Boolean(nextPage),
        responseData: response.data,
      });
    }

    const nextCursor = response.data.next;

    // If there's no next cursor/page, it means we reached the end of the data
    if (!nextCursor) {
      // Check if it's a request for the next page or the first one
      if (nextPage) {
        // If it's a next page request, append new unique orders to current order list
        setOrders(prevOrders => {
          const prevList = Array.isArray(prevOrders) ? prevOrders : [];
          const incoming = Array.isArray(response.data.results)
            ? response.data.results
            : [];
          const seenIds = new Set(prevList.map(o => o?.id));
          const uniqueIncoming = incoming.filter(
            order => order?.id == null || !seenIds.has(order.id)
          );
          return [...prevList, ...uniqueIncoming];
        });
      } else {
        // If it's a request for the first page, reset the order list
        setOrders(response.data.results);
      }
      // Notify that we reached the end of the data
      return { success: true, reachedEnd: true };
    }

    // If it's not the end...
    // Check if it's a next page request or not
    if (!nextPage) {
      // If it's a request for the first page, reset the order list
      setOrders(response.data.results);
    } else {
      // If it's a next page request, append new unique orders to current order list
      setOrders(prevOrders => {
        const prevList = Array.isArray(prevOrders) ? prevOrders : [];
        const incoming = Array.isArray(response.data.results)
          ? response.data.results
          : [];
        const seenIds = new Set(prevList.map(o => o?.id));
        const uniqueIncoming = incoming.filter(
          order => order?.id == null || !seenIds.has(order.id)
        );
        return [...prevList, ...uniqueIncoming];
      });
    }

    // Return success state and next page's URL
    return { success: true, nextPage: response.data.next };
  } catch (error) {
    if (isCanceledRequest(error)) {
      return { canceled: true };
    }
    const errPayload = error.response?.data ?? error.message;
    logError('order_client', 'Order fetch error', errPayload);
    return { success: false, error: error.response?.data ?? error.message };
  }
};

/**
 * Fetch orders for a supplier (first page) for use in dropdowns (e.g. supplier credit create).
 *
 * @param {string} token - The access token for the current user.
 * @param {number|string} supplierId - The supplier ID to filter orders by.
 * @returns {Promise<Array>} Array of order objects (response.data.results) or [] on error/empty.
 */
export const getOrdersListForSupplier = async (token, supplierId) => {
  if (!supplierId) return [];
  try {
    const url = `${BACKEND_URL}orders/?page_num=1&supplier_filter=${supplierId}`;
    const response = await axios.get(url, {
      headers: { Authorization: `Token ${token}` },
    });
    return response.data?.results ?? [];
  } catch (error) {
    logError('order_client', 'getOrdersListForSupplier failed', error.response?.data ?? error);
    return [];
  }
};

/**
 * Retrieve order details.
 *
 * Sends a GET request to the backend API to retrieve the details of a specific order denoted by orderId. If the request is successful, the order details are set in the state.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} orderId - The ID of the order whose details need to be fetched.
 * @param {Function} setOrderDetails - React state's setter function being used to update the OrderDetails state in the component.
 * @returns {Promise<Object|Array>} An object with a boolean 'success' if the order details are successfully retrieved; if not, it returns an array of error messages.
 * @throws Will throw an error if the request fails.
 */
export const getOrderDetails = async (token, orderId, setOrderDetails) => {
  try {
    // Get the order details from the server
    const response = await axios.get(`${BACKEND_URL}orders/${orderId}/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    if (process.env.NODE_ENV === 'development') {
      logInfo('order_client', 'getOrderDetails response.data', response.data);
    }

    // Set the retrieved details in state
    setOrderDetails(response.data);

    // Return a success state and data
    return { success: true, orderData: response.data };
  } catch (error) {
    logError('order_client', 'getOrderDetails failed', error.response?.data ?? error);
    return Object.values(error.response.data);
  }
};

/**
 * Upload PDF files to AWS S3.
 *
 * This function handles the process of uploading supplied PDF files to AWS S3 through predefined, pre-signed URLs.
 * It uses Axios to send POST requests to each pre-signed URL, which are generated by the backend. The details of these URLs are packed in a FormData object along with the corresponding PDF file.
 * It also creates and manages promises for each upload and handles their resolutions. On successful upload, it updates the status of that upload in an 'uploadStatuses' dictionary.
 * After handling all the uploads, it returns the 'uploadStatuses' object.
 *
 * @async
 * @param {Array} presignedUrls - An array of presigned URL objects. Each object contains the S3 presigned URL details and the associated 'frontend_id'.
 * @param {Array} files - An array of file objects. Each file object contains the file data and associated 'id'.
 * @returns {Promise<Object>} - An object containing 'uploadStatuses' corresponding to each PDF upload.
 * @throws Will throw an error if the number of presigned URLs does not match the number of files or if any file upload fails.
 */
export const uploadPdfsToS3 = async (presignedUrls, files) => {
  // Check if the number of pre-signed URLs matches the number of files
  if (presignedUrls.length !== files.length) {
    throw new Error(
      'The number of presigned URLs must match the number of files.'
    );
  }

  // Initialize the dictionary that will hold the statuses of each upload
  const uploadStatuses = {};

  // Prepare a list of upload jobs - one for each pre-signed URL/file
  const uploadPromises = presignedUrls.map(presignedPostData => {
    // Find the file to upload by matching the 'id' of the file with the 'frontend_id' in the pre-signed URL data
    const file = files.find(f => f.id === presignedPostData.frontend_id);

    // Create a new FormData object
    const formData = new FormData();

    // Loop through the pre-signed URL fields and append them to the FormData object
    // Do not append the 'pdf_id' field as this is not needed in the S3 request
    Object.keys(presignedPostData.fields).forEach(key => {
      if (key !== 'pdf_id' && key !== 'file_id') {
        formData.append(key, presignedPostData.fields[key]);
      }
    });

    // Append the file to the FormData object
    formData.append('file', file.file);

    // Make a POST request to the pre-signed URL, uploading the FormData object
    // On response (successful or not), update the upload status
    return axios.post(presignedPostData.url, formData).then(response => {
      // If the response status is successful (2xx), set the status to 'completed', otherwise 'failed'
      const finalizeId =
        presignedPostData.file_id ?? presignedPostData.pdf_id;
      uploadStatuses[finalizeId] =
        response.status >= 200 && response.status < 300
          ? 'completed'
          : 'failed';
      return response;
    });
  });

  // Execute all the upload jobs and wait until all of them finish
  try {
    const responses = await Promise.all(uploadPromises);

    // If any upload failed, throw an error
    responses.forEach((response, index) => {
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`File ${index} upload failed: ${response.statusText}`);
      }
    });

    // Return the statuses of each upload
    return { uploadStatuses: uploadStatuses };

    // If any catch any error during upload and return it
  } catch (error) {
    logError('order_client', 'Error uploading PDF files', error);
    return Object.values(error.response?.data || { error: error.message });
  }
};

/**
 * Finalize the upload status of PDF files.
 *
 * Sends a POST request to the backend API to update the upload status of PDF files. Assuming a map structure where the keys are PDF file IDs and the values are the upload status, and if successful, returns a success status.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Object} uploadStatuses - An object mapping PDF file IDs to their upload status.
 * @returns {Promise<Object>} An object with a key "success" and a boolean value indicating whether the API call was successful. If it fails, returns an array containing error messages.
 * @throws {Error} Will throw an error if the request fails.
 */
export const finalizePdfUploadStatus = async (token, uploadStatuses) => {
  try {
    // Convert the uploadStatuses object to a FormData instance
    let formData = new FormData();
    for (let key in uploadStatuses) {
      formData.append(key, uploadStatuses[key]);
    }

    // Send a POST request to the backend API to update the PDF files' upload status
    await axios.post(
      `${BACKEND_URL}orders/update_pdf_upload_status/`,
      formData,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'multipart/form-data', // Since we're uploading files, we need to set the content type to 'multipart/form-data'
        },
      }
    );

    // On success, return an object indicating the success of the operation
    return { success: true };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT_ORDER, 'finalizePdfUploadStatus failed', {
      status: error.response?.status,
    });
    const messages = formatUploadFinalizeErrors(errPayload);
    notifyUploadFinalizeFailure(messages);
    return { success: false, error: errPayload, messages };
  }
};

/**
 * Process PDF files to extract order data and optionally create orders.
 *
 * Sends a POST request to the backend API to process uploaded PDF files. Responses may include:
 * - **`created_orders`** — per-file orders created on upload (PO / direct-create path); use this
 *   as the success signal, not `ocrf_parse_summary.ocr_success`.
 * - **`presigned_urls`** — dict `{ po_files, receipt_files }` (current API) or legacy array;
 *   PO uploads use `processOrderPresignedUploads` + `update_pdf_upload_status/`. `success` is
 *   false when orders are created but S3 upload/finalize fails.
 * - **OCR handoff** — `ocrfReviewSummary`, `ocrfParseSummary` for files that need
 *   `submitReviewedOcr` after human review.
 * - **`quotes`** — one entry per file with `notify_supplier: true` so the backend queues
 *   supplier PO reminder email after PO S3 upload finalize (not at create time).
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Array} pdfFiles - An array of PDF file objects to be processed.
 * @returns {Promise<Object>} Result with `success`, `createdOrders`, `totalCreated`, `errors`,
 * `message`, `toast`, and optional OCR fields. On HTTP 4xx (except 413), the same keys may be set
 * from `error.response.data`.
 * @param {Object} [options]
 * @param {boolean} [options.excludeFromFinanceDigest=false] - When true, created orders
 *   are omitted from the daily finance reception digest.
 * @throws Will throw an error if the request fails.
 */
export const createFromPdf = async (token, pdfFiles, options = {}) => {
  try {
    const excludeFromFinanceDigest = Boolean(
      options?.excludeFromFinanceDigest
    );

    // Prepare files with IDs for tracking
    const filesWithIds = pdfFiles.map((file, index) => ({
      id: `pdf_${Date.now()}_${index}`,
      file: file,
    }));

    // Create initial request data with file metadata
    const requestData = new FormData();
    filesWithIds.forEach(fileObj => {
      requestData.append('files', fileObj.file);
      requestData.append('file_ids', fileObj.id);
    });

    const quotesPayload = filesWithIds.map(() => ({ notify_supplier: true }));
    requestData.append('quotes', JSON.stringify(quotesPayload));
    requestData.append(
      'exclude_from_finance_digest',
      excludeFromFinanceDigest ? 'true' : 'false'
    );
    logInfo(LOG_CONTEXT_ORDER, 'createFromPdf: quotes with notify_supplier', {
      fileCount: filesWithIds.length,
      excludeFromFinanceDigest,
    });

    // Send POST request to get presigned URLs and process PDFs
    const response = await axios.post(
      `${BACKEND_URL}orders/create_from_pdf/`,
      requestData,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        // Add timeout and max content length handling
        timeout: 300000, // 5 minutes timeout
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      }
    );

    let result = { success: true };

    const presignedUrls = response.data?.presigned_urls;
    if (isPresignedUrlsDict(presignedUrls)) {
      const poUrlCount = (presignedUrls.po_files || []).length;
      const receiptUrlCount = (presignedUrls.receipt_files || []).length;
      if (
        (poUrlCount > 0 || receiptUrlCount > 0) &&
        filesWithIds.length > 0
      ) {
        logInfo(LOG_CONTEXT_ORDER, 'createFromPdf: dict presigned_urls', {
          poUrlCount,
          receiptUrlCount,
          pendingFileCount: filesWithIds.length,
        });
        const uploadOk = await processOrderPresignedUploads(
          token,
          presignedUrls,
          {
            poPending: filesWithIds,
            receiptPending: [],
          }
        );
        if (!uploadOk.success) {
          result.success = false;
        }
      }
    } else if (Array.isArray(presignedUrls) && presignedUrls.length > 0) {
      const uploadResult = await uploadPdfsToS3(presignedUrls, filesWithIds);

      if (uploadResult && uploadResult.uploadStatuses) {
        const finalizeResult = await finalizePdfUploadStatus(
          token,
          uploadResult.uploadStatuses
        );
        if (finalizeResult && !finalizeResult.success) {
          result.success = false;
          notifyUploadFinalizeFailure(
            finalizeResult.messages || finalizeResult.error
          );
        }
      } else {
        result.success = false;
      }
    } else if (filesWithIds.length > 0) {
      const createdCount = Array.isArray(response.data?.created_orders)
        ? response.data.created_orders.length
        : 0;
      if (createdCount > 0) {
        logError(
          LOG_CONTEXT_ORDER,
          'createFromPdf: orders created but no presigned_urls for PO upload',
          { createdCount }
        );
        result.success = false;
      }
    }

    // Google Vision OCR handoff: per-file summaries from POST body (see backend handoff for `source_mode` on ocrf_parse_summary)
    const reviewSummary = response.data.ocrf_review_summary;
    const parseSummary = response.data.ocrf_parse_summary;
    result.ocrfReviewSummary = Array.isArray(reviewSummary) ? reviewSummary : [];
    result.ocrfParseSummary = Array.isArray(parseSummary) ? parseSummary : [];
    result.reviewAvailable = Boolean(response.data.review_available);
    result.ocrfLegacyFallbackReview = response.data.ocrf_legacy_fallback_review ?? null;
    if (result.ocrfReviewSummary.length || result.ocrfParseSummary.length) {
      logInfo(LOG_CONTEXT_ORDER, 'createFromPdf: OCR summary fields present', {
        ocrfReviewSummaryCount: result.ocrfReviewSummary.length,
        ocrfParseSummaryCount: result.ocrfParseSummary.length,
        reviewAvailable: result.reviewAvailable,
      });
    }

    result.createdOrders = Array.isArray(response.data.created_orders)
      ? response.data.created_orders
      : [];
    result.extractedData = response.data.extracted_data;
    result.totalFilesProcessed = response.data.total_files_processed;
    result.totalCreated = response.data.total_created;
    result.errors = response.data.errors;
    result.message = response.data.message;
    if (result.createdOrders.length > 0) {
      logInfo(LOG_CONTEXT_ORDER, 'createFromPdf: orders created on upload', {
        createdOrdersCount: result.createdOrders.length,
        totalCreated: result.totalCreated,
      });
    }
    result.toast = () => {
      const created = result.createdOrders.length || Number(result.totalCreated || 0);
      const message = created
        ? `${created} order(s) created from PDF upload.`
        : `PDF processing completed for ${
            response.data.total_files_processed || pdfFiles.length || 0
          } file(s). Review extracted data before submission.`;

      showToast(message, 'success', 'top-right', 3000);
    };

    return result;
  } catch (error) {
    // Enhanced error handling for different types of errors
    if (error.response) {
      // Server responded with error status
      if (error.response.status === 413) {
        return {
          success: false,
          error: 'Request Entity Too Large',
          message:
            'The total file size exceeds the server limit. Please try uploading fewer files at once.',
          details: error.response.data,
        };
      } else if (error.response.status >= 500) {
        return {
          success: false,
          error: 'Server Error',
          message: 'Server encountered an error. Please try again later.',
          details: error.response.data,
        };
      } else {
        // Handle 400 and other client errors - extract detailed errors; include OCR handoff for review UIs
        const errorData = error.response.data || {};
        const reviewSummary = errorData.ocrf_review_summary;
        const parseSummary = errorData.ocrf_parse_summary;
        const ocrfReviewSummary = Array.isArray(reviewSummary) ? reviewSummary : [];
        const ocrfParseSummary = Array.isArray(parseSummary) ? parseSummary : [];
        if (ocrfReviewSummary.length || ocrfParseSummary.length) {
          logInfo(LOG_CONTEXT_ORDER, 'createFromPdf: OCR fields on client error response', {
            status: error.response.status,
            ocrfReviewSummaryCount: ocrfReviewSummary.length,
            ocrfParseSummaryCount: ocrfParseSummary.length,
          });
        }
        const createdOrders = Array.isArray(errorData.created_orders)
          ? errorData.created_orders
          : [];
        if (createdOrders.length > 0) {
          logInfo(LOG_CONTEXT_ORDER, 'createFromPdf: partial created_orders on error response', {
            status: error.response.status,
            createdOrdersCount: createdOrders.length,
          });
        }
        return {
          success: false,
          error: `HTTP ${error.response.status}`,
          message: errorData.message || 'Upload failed',
          errors: errorData.errors || [errorData.message || 'Upload failed'],
          details: errorData,
          ocrfReviewSummary,
          ocrfParseSummary,
          reviewAvailable: Boolean(errorData.review_available),
          ocrfLegacyFallbackReview: errorData.ocrf_legacy_fallback_review ?? null,
          createdOrders,
          totalCreated: errorData.total_created,
          extractedData: errorData.extracted_data,
        };
      }
    } else if (error.request) {
      // Request was made but no response received
      return {
        success: false,
        error: 'Network Error',
        message:
          'No response from server. Please check your connection and try again.',
        details: error.message,
      };
    } else {
      // Something else happened
      return {
        success: false,
        error: 'Upload Error',
        message: error.message || 'Failed to upload PDFs',
        details: error,
      };
    }
  }
};

/**
 * Submit a user-reviewed OCR extraction preview for final order upsert.
 *
 * When the upload phase had `order_lookup_status: not_found`, the payload must include
 * top-level `selected_quote_id` (quote PK) so the backend can create an order from that
 * quote. For an existing PO (`found`), the field is optional and ignored.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Object} payload - JSON with `received_by`, `ocr_extraction_preview`, and optional
 *   `selected_quote_id` (number) for the not-found / create path.
 * @returns {Promise<Object>} Normalized response with `success`, `orderId`, `orderUpsertAction`,
 * `postExtractionReview`, `matchedQuoteId`, `quoteMatchScore`, `lineMatchSummary`, `submittedPreview`,
 * optional `details` on failure (HTTP 400: `error`, `review_errors`, `post_extraction_review`).
 */
export const submitReviewedOcr = async (token, payload) => {
  logInfo(LOG_CONTEXT_ORDER, 'submitReviewedOcr request', {
    hasSelectedQuoteId: payload?.selected_quote_id != null,
    itemCount: Array.isArray(payload?.ocr_extraction_preview?.items)
      ? payload.ocr_extraction_preview.items.length
      : 0,
  });
  try {
    const response = await axios.post(
      `${BACKEND_URL}orders/submit_reviewed_ocr/`,
      payload,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    const data = response?.data || {};
    const normalized = {
      success: Boolean(data.success),
      orderId: data.order_id ?? null,
      orderUpsertAction: data.order_upsert_action || '',
      postExtractionReview: data.post_extraction_review ?? null,
      matchedQuoteId: data.matched_quote_id ?? null,
      quoteMatchScore: data.quote_match_score ?? null,
      lineMatchSummary: data.line_match_summary || null,
      submittedPreview: data.submitted_preview || null,
      message: data.message || '',
      raw: data,
    };
    if (normalized.success) {
      logInfo(LOG_CONTEXT_ORDER, 'submitReviewedOcr succeeded', {
        orderId: normalized.orderId,
        orderUpsertAction: normalized.orderUpsertAction,
        matchedQuoteId: normalized.matchedQuoteId,
      });
    }
    return normalized;
  } catch (error) {
    const data = error.response?.data || {};
    logError(LOG_CONTEXT_ORDER, 'submitReviewedOcr failed', data || error.message);
    return {
      success: false,
      error: data.error || data.message || error.message || 'Submit reviewed OCR failed',
      message: data.message || '',
      details: data,
      orderId: data.order_id ?? null,
      orderUpsertAction: data.order_upsert_action || '',
      postExtractionReview: data.post_extraction_review ?? null,
      matchedQuoteId: data.matched_quote_id ?? null,
      quoteMatchScore: data.quote_match_score ?? null,
      lineMatchSummary: data.line_match_summary || null,
      submittedPreview: data.submitted_preview || null,
    };
  }
};

/**
 * Get detailed order information with lazy loading.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} orderId - The ID of the order to fetch details for.
 * @returns {Promise<Object>} - The detailed order data.
 * @throws Will throw an error if the request fails.
 */
export const getOrderDetailsLazy = async (token, orderId) => {
  const resolvedOrderId = typeof orderId === 'object' ? orderId?.id : orderId;
  try {
    // Check cache first
    const cached = OrderCache.get(resolvedOrderId);
    if (cached) {
      if (process.env.NODE_ENV === 'development') {
        logInfo('order_client', 'getOrderDetailsLazy returning cached data', { orderId, cached });
      }
      return cached;
    }

    // Send a GET request to the get_details endpoint
    const response = await axios.get(
      `${BACKEND_URL}orders/${resolvedOrderId}/get_details/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    if (process.env.NODE_ENV === 'development') {
      logInfo('order_client', 'getOrderDetailsLazy response.data', { orderId, data: response.data });
    }

    // Cache the result
    OrderCache.set(resolvedOrderId, response.data);

    return response.data;
  } catch (error) {
    logError('order_client', 'Error fetching order details', error);
    throw error;
  }
};

/**
 * Get detailed product information for order items.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} orderId - The ID of the order to fetch products for.
 * @returns {Promise<Array>} - Array of detailed product information.
 * @throws Will throw an error if the request fails.
 */
export const getOrderProducts = async (token, orderId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}orders/${orderId}/get_products/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    logError('order_client', 'Error fetching order products', error);
    throw error;
  }
};

/**
 * Get detailed supplier information for an order.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} orderId - The ID of the order to fetch supplier for.
 * @returns {Promise<Object>} - Detailed supplier information.
 * @throws Will throw an error if the request fails.
 */
export const getOrderSupplier = async (token, orderId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}orders/${orderId}/get_supplier/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    logError('order_client', 'Error fetching order supplier', error);
    throw error;
  }
};

/**
 * Get detailed quote information for an order.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} orderId - The ID of the order to fetch quote for.
 * @returns {Promise<Object>} - Detailed quote information.
 * @throws Will throw an error if the request fails.
 */
export const getOrderQuote = async (token, orderId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}orders/${orderId}/get_quote/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    logError('order_client', 'Error fetching order quote', error);
    throw error;
  }
};
